import { Directive, ElementRef, HostListener, OnDestroy, Renderer2 } from '@angular/core';
import MailSpellChecker, { POPULAR_DOMAINS, POPULAR_TLDS } from '@zootools/email-spell-checker';

// Additional Czech/Slovak domains not in the library's default list
const EXTRA_DOMAINS = [
    'seznam.cz', 'centrum.cz', 'email.cz', 'volny.cz', 'atlas.cz',
    'quick.cz', 'post.cz', 'tiscali.cz', 'azet.sk', 'zoznam.sk', 'ngretail.cz'
];

const EXTRA_TLDS = ['cz', 'sk'];

@Directive({
    // tslint:disable-next-line:directive-selector
    selector: 'input[hcEmailSpellCheck]'
})
export class EmailSpellCheckerDirective implements OnDestroy {

    private suggestionEl: HTMLElement | null = null;
    private currentValue = '';

    constructor(
        private el: ElementRef<HTMLInputElement>,
        private renderer: Renderer2
    ) {}

    @HostListener('input', ['$event'])
    onInput(event: Event) {
        this.currentValue = (event.target as HTMLInputElement).value;
    }

    @HostListener('blur')
    onBlur() {
        // Always read directly from the element to avoid stale state
        this.currentValue = this.el.nativeElement.value;
        this.checkEmail();
    }

    private checkEmail() {
        const email = this.currentValue;
        if (!email || !email.includes('@')) {
            this.removeSuggestion();
            return;
        }

        const suggestion = MailSpellChecker.run({
            email,
            domains: [...POPULAR_DOMAINS, ...EXTRA_DOMAINS],
            topLevelDomains: [...POPULAR_TLDS, ...EXTRA_TLDS]
        });

        if (suggestion && suggestion.full !== email) {
            this.showSuggestion(suggestion.full);
        } else {
            this.removeSuggestion();
        }
    }

    private findInsertionPoint(): { container: HTMLElement; insertAfter: HTMLElement } | null {
        // Find mat-form-field and insert the suggestion AFTER it (not inside)
        // to avoid pointer-events:none on the subscript wrapper
        let el: HTMLElement = this.el.nativeElement;

        while (el && el.parentElement) {
            el = el.parentElement;
            if (el.tagName && el.tagName.toLowerCase() === 'mat-form-field') {
                return { container: el.parentElement, insertAfter: el };
            }
        }
        // Fallback: insert after the input's direct parent
        const parent = this.el.nativeElement.parentElement;
        return parent ? { container: parent.parentElement ?? parent, insertAfter: parent } : null;
    }

    private showSuggestion(suggestedEmail: string) {
        this.removeSuggestion();

        const insertion = this.findInsertionPoint();
        if (!insertion) {
            return;
        }
        const { container, insertAfter } = insertion;

        this.suggestionEl = this.renderer.createElement('div') as HTMLElement;
        this.renderer.addClass(this.suggestionEl, 'hc-email-spellcheck-hint');
        this.renderer.setStyle(this.suggestionEl, 'display', 'inline-flex');
        this.renderer.setStyle(this.suggestionEl, 'align-items', 'center');
        this.renderer.setStyle(this.suggestionEl, 'gap', '4px');
        this.renderer.setStyle(this.suggestionEl, 'font-size', '12px');
        this.renderer.setStyle(this.suggestionEl, 'margin-top', '2px');
        this.renderer.setStyle(this.suggestionEl, 'margin-bottom', '4px');
        this.renderer.setStyle(this.suggestionEl, 'padding', '3px 8px');
        this.renderer.setStyle(this.suggestionEl, 'background-color', '#fff3e0');
        this.renderer.setStyle(this.suggestionEl, 'border', '1px solid #ffb74d');
        this.renderer.setStyle(this.suggestionEl, 'border-radius', '4px');
        this.renderer.setStyle(this.suggestionEl, 'color', '#5d4037');
        this.renderer.setStyle(this.suggestionEl, 'pointer-events', 'auto');
        this.renderer.setStyle(this.suggestionEl, 'user-select', 'none');

        const icon: HTMLElement = this.renderer.createElement('span');
        this.renderer.setStyle(icon, 'font-family', 'Material Icons');
        this.renderer.setStyle(icon, 'font-size', '14px');
        this.renderer.setStyle(icon, 'line-height', '1');
        this.renderer.setStyle(icon, 'vertical-align', 'middle');
        this.renderer.setStyle(icon, 'color', '#e65100');
        this.renderer.appendChild(icon, this.renderer.createText('info_outline'));
        this.renderer.appendChild(this.suggestionEl, icon);

        const textNode = this.renderer.createText('Mysleli jste:\u00a0');
        this.renderer.appendChild(this.suggestionEl, textNode);

        const link: HTMLElement = this.renderer.createElement('a');
        this.renderer.setStyle(link, 'cursor', 'pointer');
        this.renderer.setStyle(link, 'color', '#1565c0');
        this.renderer.setStyle(link, 'text-decoration', 'underline');
        this.renderer.setStyle(link, 'font-weight', '600');
        this.renderer.setStyle(link, 'pointer-events', 'auto');
        const linkText = this.renderer.createText(suggestedEmail);
        this.renderer.appendChild(link, linkText);

        this.renderer.listen(link, 'mousedown', (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            this.el.nativeElement.value = suggestedEmail;
            this.el.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
            this.el.nativeElement.dispatchEvent(new Event('change', { bubbles: true }));
            this.removeSuggestion();
        });

        this.renderer.appendChild(this.suggestionEl, link);

        const nextSibling = insertAfter.nextSibling;
        if (nextSibling) {
            this.renderer.insertBefore(container, this.suggestionEl, nextSibling);
        } else {
            this.renderer.appendChild(container, this.suggestionEl);
        }
    }

    private removeSuggestion() {
        if (this.suggestionEl && this.suggestionEl.parentElement) {
            this.renderer.removeChild(this.suggestionEl.parentElement, this.suggestionEl);
            this.suggestionEl = null;
        }
    }

    ngOnDestroy() {
        this.removeSuggestion();
    }
}
